import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Link } from "gatsby";
import { Container } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { recordClick, isUnlocked, clear } from "../../easter-egg/state";
import Launcher from "../../easter-egg/Launcher";

const Section = styled.section`
  padding: 96px 0;
  background: var(--nd-black);
`;

const SectionLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 0 0 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Card = styled(Link)`
  position: relative;
  display: block;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-left: 3px solid transparent;
  border-radius: 12px;
  padding: 24px 32px;
  text-decoration: none !important;
  color: inherit !important;
  transition:
    border-color 200ms,
    border-left-color 200ms,
    transform 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  will-change: transform;

  &:hover {
    border-color: var(--nd-text-display);
    border-left-color: var(--nd-text-display);
    transform: scale(1.02);
  }
`;

/* Corner badge: top-left Doto numeral */
const Num = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 24px;
  font-weight: 700;
  color: var(--nd-text-display);
  line-height: 1;
  letter-spacing: 0.04em;
`;

/* Invisible button wrapper around the Doto numeral. No hover affordance,
   no cursor change — reads as decorative. The 44×44 hit target sits
   under the digit so mobile taps work, but the whole card still
   navigates on click anywhere else. */
const NumButton = styled.button`
  background: transparent;
  border: 0;
  padding: 10px;
  margin: -10px;
  font: inherit;
  color: inherit;
  letter-spacing: inherit;
  cursor: default;
  display: inline-block;
  min-width: 44px;
  min-height: 44px;
  &:focus { outline: none; }
  &:focus-visible { outline: none; }
`;

/* Top-right: tiny live status dot */
const StatusDot = styled.span`
  position: absolute;
  top: 22px;
  right: 22px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--nd-success);
  box-shadow: 0 0 0 2px rgba(74, 158, 92, 0.18);
`;

const Body = styled.div`
  padding-top: 32px;
`;

const Name = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin: 0 0 8px;
`;

const Desc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  margin: 0 0 16px;
`;

const Cta = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-display);
  &::after { content: ' →'; }
`;

const ITEMS = [
  {
    n: "01",
    name: "Retainers",
    desc: "Senior DevOps embedded in your team. Monthly hours, weekly reviews, no hiring cycle.",
    href: "/pricing",
    cta: "See packages",
  },
  {
    n: "02",
    name: "Mentorship + Staffing",
    desc: "We place a Dioscuro junior in your team and mentor them. If they're great, hire them — no placement fee.",
    href: "/staffing",
    cta: "Browse the roster",
  },
  {
    n: "03",
    name: "Academy",
    desc: "Career Path, Labs, and a $0-upfront Team Track. Where the juniors we staff come from.",
    href: "/academy",
    cta: "Open the academy",
  },
];

const SCRAMBLE_CHARS = "0123456789";
const SCRAMBLE_STEPS = 3;
const SCRAMBLE_STEP_MS = 60; // 3 steps × 60ms ≈ 180ms, settles ~ 200ms total
const STAGGER_MS = 80;
const TRANSITION = "cubic-bezier(0.25, 0.1, 0.25, 1)";

const ScrambledNum = ({ target, start, delay, onClick }) => {
  const [text, setText] = useState(target);
  const timeouts = useRef([]);

  useEffect(() => {
    if (!start) {
      setText(target);
      return undefined;
    }
    // Clear any in-flight scramble
    timeouts.current.forEach((t) => clearTimeout(t));
    timeouts.current = [];

    const kickoff = setTimeout(() => {
      for (let step = 0; step < SCRAMBLE_STEPS; step += 1) {
        const t = setTimeout(() => {
          const scrambled = target
            .split("")
            .map(() => SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)])
            .join("");
          setText(scrambled);
        }, step * SCRAMBLE_STEP_MS);
        timeouts.current.push(t);
      }
      const settle = setTimeout(() => {
        setText(target);
      }, SCRAMBLE_STEPS * SCRAMBLE_STEP_MS);
      timeouts.current.push(settle);
    }, delay);
    timeouts.current.push(kickoff);

    return () => {
      timeouts.current.forEach((t) => clearTimeout(t));
      timeouts.current = [];
    };
    // We intentionally only re-run when `start` flips on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  return (
    <Num>
      <NumButton type="button" onClick={onClick} aria-label={target}>
        {text}
      </NumButton>
    </Num>
  );
};

const ProductsSplit = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const { user } = useAuth();
  const [gameOn, setGameOn] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  // Always stop the click from bubbling up to the parent <Card to={...}> Link.
  // Public users (`!user`) get a no-op — the numeral looks decorative and acts
  // like dead space inside the card. Signed-in users advance the sequence.
  const handleNumClick = (cardN) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!user) return;
    const state = recordClick(cardN);
    if (isUnlocked(state)) {
      clear();
      setGameOn(true);
    }
  };

  const wrapperStyle = (i) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(12px)",
    transition: `opacity 400ms ${TRANSITION} ${i * STAGGER_MS}ms, transform 400ms ${TRANSITION} ${i * STAGGER_MS}ms`,
  });

  if (gameOn) {
    return (
      <Section ref={ref}>
        <Launcher active onExit={() => setGameOn(false)} />
      </Section>
    );
  }

  return (
    <Section ref={ref}>
      <Container>
        <SectionLabel>Three ways to work with us</SectionLabel>
        <Grid>
          {ITEMS.map((it, i) => (
            <div key={it.n} style={wrapperStyle(i)}>
              <Card to={it.href}>
                <ScrambledNum
                  target={it.n}
                  start={visible}
                  delay={i * STAGGER_MS + 200}
                  onClick={handleNumClick(it.n)}
                />
                <StatusDot aria-label="live" />
                <Body>
                  <Name>{it.name}</Name>
                  <Desc>{it.desc}</Desc>
                  <Cta>{it.cta}</Cta>
                </Body>
              </Card>
            </div>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default ProductsSplit;
